import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "text-xs" }
const _hoisted_2 = { class: "text-xs" }

import { IonApp, IonPage } from '@ionic/vue';
import { RouterView, useRoute } from 'vue-router';
import { App } from '@capacitor/app';
import ModalContainer from '@/components/Molecules/Modals/ModalContainer.vue';
import useCheckAppVersionModals from '@/composables/useCheckAppVersionModals';
import useNetworkStatus from '@/composables/useNetworkStatus';
import { computed, onMounted, onUnmounted, watch } from 'vue';
import useDialog from '@/composables/useDialog';
import useSession from '@/composables/useSession';
import useListenAudio from '@/composables/useListenAudio';
import useRecording from '@/composables/useRecording';
import SessionStatus from '@/models/SessionStatus';
import EventType from '@/models/ElasticApmRum/EventType';
import ProgressStatus from '@/models/ProgressStatus';
import useLoader from '@/composables/useLoader';
import useUserAttention from '@/composables/v2/useFullscreen';
import useElasticApmRumMetricsAdapter from '@/composables/v2/metrics/adapters/useElasticApmRumMetricsAdapter';


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const {
    showModalIfAppMustUpdate,
    appMustUpdateModal,
    errorAppVersionCheckModal,
    packagedVersion,
    currentVersion,
    errorAppVersionCheckMessage,
} = useCheckAppVersionModals();

const { createNewDialog, openModal, closeModal } = useDialog();
const { resetSession, currentSessionStatus, currentProgressStatus } = useSession();
const { startSpanWithId, stopSpanWithId, sendEvent } = useElasticApmRumMetricsAdapter();
const { stopListen } = useListenAudio();
const { stopListenToRecord } = useRecording;
const { isFullscreen, enterFullscreen, isDistracted, enableUnloadProtection, disableUnloadProtection, setActive } =
    useUserAttention();
const route = useRoute();

const sessionEndedModal = createNewDialog();
const sessionTimedOutModal = createNewDialog();
const sessionPausedModal = createNewDialog();
const userAttentionModal = createNewDialog();

let sessionPausedSpanId: string | undefined;

function onSessionEnded(): void {
    stopListen();
    stopListenToRecord();

    sendEvent(SessionStatus.ENDED, EventType.SESSION_STATUS);
    openModal(sessionEndedModal);
}

function onSessionTimedOut(): void {
    stopListen();
    stopListenToRecord();

    sendEvent(SessionStatus.ENDED, EventType.SESSION_STATUS);
    openModal(sessionTimedOutModal);
}

function onSessionPaused(): void {
    stopListen();
    stopListenToRecord();

    openModal(sessionPausedModal);
    if (!sessionPausedSpanId) {
        sessionPausedSpanId = startSpanWithId(SessionStatus.PAUSED, EventType.SESSION_STATUS);
    }
}

function onSessionResumed(): void {
    closeModal(sessionPausedModal);
    if (sessionPausedSpanId) {
        stopSpanWithId(sessionPausedSpanId);
        sessionPausedSpanId = undefined;
    }
}

function acknowledgeSessionEnded(): void {
    resetSession();
    closeModal(sessionEndedModal);
    closeModal(sessionTimedOutModal);
}

watch(currentSessionStatus, (status) => {
    if (status === SessionStatus.ENDED) {
        onSessionEnded();
    }
    if (status === SessionStatus.TIMED_OUT) {
        onSessionTimedOut();
    }
    if (status === SessionStatus.PAUSED) {
        onSessionPaused();
    }
    if (status === SessionStatus.STARTED && currentProgressStatus.value !== ProgressStatus.PAUSED) {
        onSessionResumed();
    }
});

watch(currentProgressStatus, (status) => {
    if (status === ProgressStatus.PAUSED) {
        onSessionPaused();
    } else if (currentSessionStatus.value === SessionStatus.STARTED) {
        onSessionResumed();
    }
});

// add listener for app state changes using capacitor
App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
        // app is in foreground
        showModalIfAppMustUpdate();
    }
});

const { addNetworkStatusListener, networkStatus } = useNetworkStatus();
addNetworkStatusListener();

const offlineModal = createNewDialog();

watch(networkStatus, (newStatus) => {
    if (!newStatus.connected) {
        openModal(offlineModal, false);
    } else {
        closeModal(offlineModal);
    }
});

const { isLoading } = useLoader();
const loadingModal = createNewDialog();

watch(isLoading, (isLoadingStatus) => {
    if (isLoadingStatus) {
        openModal(loadingModal, false);
    } else {
        closeModal(loadingModal);
    }
});

const isTaskRoute = computed(() => typeof route.name === 'string' && route.name.startsWith('tasks.'));
watch(isTaskRoute, (active) => {
    setActive(active);
});
onMounted(() => setActive(isTaskRoute.value));
onUnmounted(() => setActive(false));

const checkIfDistractedModalNeedToBeOpen = (fullscreen, distracted) => {
    if (!isTaskRoute.value) {
        disableUnloadProtection();
        return;
    }

    enableUnloadProtection();
    if (!fullscreen || distracted) {
        openModal(userAttentionModal, false);
    } else {
        closeModal(userAttentionModal);
    }
};

onMounted(() => {
    checkIfDistractedModalNeedToBeOpen(isFullscreen.value, isDistracted.value);
});

watch([isFullscreen, isDistracted], ([fullscreen, distracted]) => {
    checkIfDistractedModalNeedToBeOpen(fullscreen, distracted);
});

watch(
    () => route.name,
    () => checkIfDistractedModalNeedToBeOpen(isFullscreen.value, isDistracted.value),
    { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonApp), null, {
    default: _withCtx(() => [
      _createVNode(_unref(IonPage), { class: "page" }, {
        default: _withCtx(() => [
          _createVNode(_unref(RouterView)),
          _createVNode(ModalContainer, {
            "dialog-id": _unref(offlineModal),
            "out-side-closable": false,
            "modal-closable": false
          }, {
            texte: _withCtx(() => _cache[0] || (_cache[0] = [
              _createTextVNode(" Vous êtes hors ligne "),
              _createElementVNode("br", null, null, -1),
              _createElementVNode("br", null, null, -1),
              _createElementVNode("div", { class: "text-xl" }, "Demandez de l'aide pour continuer!", -1)
            ])),
            _: 1
          }, 8, ["dialog-id"]),
          _createVNode(ModalContainer, {
            "dialog-id": _unref(appMustUpdateModal),
            "out-side-closable": false,
            "modal-closable": false
          }, {
            texte: _withCtx(() => [
              _cache[2] || (_cache[2] = _createTextVNode("Votre application doit être mise à jour, demandez de l'aide ! ")),
              _cache[3] || (_cache[3] = _createElementVNode("br", null, null, -1)),
              _cache[4] || (_cache[4] = _createElementVNode("br", null, null, -1)),
              _createElementVNode("div", _hoisted_1, [
                _createTextVNode(" version actuelle : " + _toDisplayString(_unref(currentVersion)), 1),
                _cache[1] || (_cache[1] = _createElementVNode("br", null, null, -1)),
                _createTextVNode(" version tablette : " + _toDisplayString(_unref(packagedVersion)), 1)
              ])
            ]),
            _: 1
          }, 8, ["dialog-id"]),
          _createVNode(ModalContainer, {
            "dialog-id": _unref(errorAppVersionCheckModal),
            "out-side-closable": false,
            "modal-closable": false
          }, {
            texte: _withCtx(() => [
              _cache[5] || (_cache[5] = _createTextVNode("Une erreur est survenue lors de la vérification de la version de l'application, demandez de l'aide !")),
              _cache[6] || (_cache[6] = _createElementVNode("br", null, null, -1)),
              _cache[7] || (_cache[7] = _createElementVNode("br", null, null, -1)),
              _createElementVNode("div", _hoisted_2, "Error: " + _toDisplayString(_unref(errorAppVersionCheckMessage)), 1)
            ]),
            _: 1
          }, 8, ["dialog-id"]),
          _createVNode(ModalContainer, {
            class: "z-[1002]",
            "dialog-id": _unref(loadingModal),
            "out-side-closable": false,
            "modal-closable": false
          }, {
            texte: _withCtx(() => _cache[8] || (_cache[8] = [
              _createTextVNode(" Chargement... ")
            ])),
            _: 1
          }, 8, ["dialog-id"]),
          _createVNode(ModalContainer, {
            "dialog-id": _unref(sessionEndedModal),
            "out-side-closable": false,
            "btn-right-text": "OK",
            "btn-right-function": acknowledgeSessionEnded,
            "modal-closable": false
          }, {
            texte: _withCtx(() => _cache[9] || (_cache[9] = [
              _createTextVNode(" La session est terminée. Merci pour ta contribution !"),
              _createElementVNode("br", null, null, -1),
              _createTextVNode(" Clique sur ok pour être redirigé vers la page d'accueil. ")
            ])),
            _: 1
          }, 8, ["dialog-id"]),
          _createVNode(ModalContainer, {
            "dialog-id": _unref(sessionTimedOutModal),
            "out-side-closable": false,
            "btn-right-text": "OK",
            "btn-right-function": acknowledgeSessionEnded,
            "modal-closable": false
          }, {
            texte: _withCtx(() => _cache[10] || (_cache[10] = [
              _createTextVNode(" Le temps d'activité est terminé. Merci pour ta participation ! ")
            ])),
            _: 1
          }, 8, ["dialog-id"]),
          _createVNode(ModalContainer, {
            "dialog-id": _unref(sessionPausedModal),
            "out-side-closable": false,
            "modal-closable": false
          }, {
            texte: _withCtx(() => _cache[11] || (_cache[11] = [
              _createTextVNode(" La session a été mise en pause. Elle reprendra dans quelques instants. ")
            ])),
            _: 1
          }, 8, ["dialog-id"]),
          _createVNode(ModalContainer, {
            "dialog-id": _unref(userAttentionModal),
            "out-side-closable": false,
            "modal-closable": false,
            "btn-right-text": !_unref(isFullscreen) ? 'Activer le plein écran' : undefined,
            "btn-right-function": !_unref(isFullscreen) ? _unref(enterFullscreen) : undefined
          }, {
            texte: _withCtx(() => [
              (!_unref(isFullscreen) && _unref(isDistracted))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[12] || (_cache[12] = _createTextVNode(" Tu as quitté l'écran ")),
                    _cache[13] || (_cache[13] = _createElementVNode("strong", null, "et", -1)),
                    _cache[14] || (_cache[14] = _createTextVNode(" tu n'es pas en plein écran.")),
                    _cache[15] || (_cache[15] = _createElementVNode("br", null, null, -1)),
                    _cache[16] || (_cache[16] = _createTextVNode(" Reviens dans l'application et active le plein écran pour continuer.")),
                    _cache[17] || (_cache[17] = _createElementVNode("br", null, null, -1)),
                    _cache[18] || (_cache[18] = _createElementVNode("br", null, null, -1)),
                    _cache[19] || (_cache[19] = _createElementVNode("small", null, "Astuce Windows : appuie sur F11", -1)),
                    _cache[20] || (_cache[20] = _createElementVNode("br", null, null, -1)),
                    _cache[21] || (_cache[21] = _createElementVNode("small", null, "Astuce Mac : appuie sur Ctrl + ⌘ + F", -1))
                  ], 64))
                : (!_unref(isFullscreen))
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _cache[22] || (_cache[22] = _createTextVNode(" L'application doit être utilisée en mode plein écran.")),
                      _cache[23] || (_cache[23] = _createElementVNode("br", null, null, -1)),
                      _cache[24] || (_cache[24] = _createTextVNode(" Merci d'activer le plein écran pour continuer.")),
                      _cache[25] || (_cache[25] = _createElementVNode("br", null, null, -1)),
                      _cache[26] || (_cache[26] = _createElementVNode("br", null, null, -1)),
                      _cache[27] || (_cache[27] = _createElementVNode("small", null, "Astuce Windows : appuie sur F11", -1)),
                      _cache[28] || (_cache[28] = _createElementVNode("br", null, null, -1)),
                      _cache[29] || (_cache[29] = _createElementVNode("small", null, "Astuce Mac : appuie sur Ctrl + ⌘ + F", -1))
                    ], 64))
                  : (_unref(isDistracted))
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                        _createTextVNode(" Tu as l’air distrait. Reviens dans l’application pour continuer. ")
                      ], 64))
                    : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["dialog-id", "btn-right-text", "btn-right-function"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})